import React from 'react';
import { useAtomValue } from 'jotai';
import PropTypes from 'prop-types';
import './PlayerPage.css';
import Layout from '@/Layout';
import VideoDescription from '@/Pages/VideoPlayer/components/VideoDescription';
import ClassVideoTrailer from '@/Pages/VideoPlayer/components/VideoCommon';
import { userInfoAtom } from '@/data/atoms/utilsAtoms';
import { useFetchVideoById } from '@/data/swrs/videos';
import { useAuthorizationForNonSubscribeUser } from '@/hook/useAuthorizationForNonSubscribeUser';
import { userRoleTypes } from '@/utils/userRoleTypes';
import PlayerPageLayouts from '@/Pages/VideoPlayer/Layouts/PlayerPageLayouts';
import { ContentView } from '@/Pages/VideoPlayer/utils/queryParamsKeys';
import { useVideoDetailsFromParams } from '@/Pages/VideoPlayer/hooks/useVideoDetailsFromParams';
import MuxVideoPlayer from '@/Pages/VideoPlayer/components/MuxVideoPlayer';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { resolveAdvancedJumpsLead, fetchPromoVideo } from '@/service/promoAdvancedJumps';
import { useEffect, useState } from 'react';
import localStorageKeys from '@/constants/localStorageKeys';

function PlayerPage() {
	const { videoId: paramVideoId, typeOfContentView } = useVideoDetailsFromParams();
	const userData = useAtomValue(userInfoAtom);
	const navigate = useNavigate();
	const location = useLocation();
	const [searchParams] = useSearchParams();
	const promoToken = searchParams.get('promoToken') || '';
	const promoVideo = searchParams.get('promoVideo') || '';
	const publicPlaybackId = searchParams.get('playbackId') || '';
	const videoId = paramVideoId || searchParams.get('v') || '';
	const hasPromoToken = Boolean(promoToken);
	const hasPublicPlaybackId = Boolean(publicPlaybackId);
	const hasPromoAccess = hasPromoToken || hasPublicPlaybackId;
	// Start optimistically as valid if token or public playbackId exists — prevents auth redirect during async verification
	const [promoValid, setPromoValid] = useState(hasPromoAccess);
	const [promoChecked, setPromoChecked] = useState(!hasPromoAccess);
	const [promoVideoData, setPromoVideoData] = useState(null);

	useEffect(() => {
		if (hasPromoAccess && location.pathname === '/PlayerPage') {
			navigate(`/v/PlayerPage${location.search}`, { replace: true });
		}
	}, [hasPromoAccess, location.pathname, location.search, navigate]);

	// Verify promo token and fetch promo video in one shot
	useEffect(() => {
		if (!promoToken) return;
		console.log('[PlayerPage] promoToken found, starting fetch. promoVideo:', promoVideo);
		Promise.all([
			resolveAdvancedJumpsLead(promoToken),
			fetchPromoVideo(promoToken, promoVideo)
		]).then(([resolveRes, videoRes]) => {
			console.log('[PlayerPage] resolveRes:', resolveRes);
			console.log('[PlayerPage] videoRes:', videoRes);
			if (resolveRes.error || !resolveRes.data) {
				console.warn('[PlayerPage] promoToken invalid or lead not found:', resolveRes.error);
				setPromoValid(false);
			} else {
				console.log('[PlayerPage] promoToken valid');
				setPromoValid(true);
			}
			if (!videoRes.error && videoRes.data) {
				console.log('[PlayerPage] promoVideoData set:', { title: videoRes.data?.title, muxVideoLink: videoRes.data?.muxVideoLink });
				setPromoVideoData(videoRes.data);
			} else {
				console.warn('[PlayerPage] videoRes error or no data:', videoRes.error, videoRes);
			}
		}).catch((err) => {
			console.error('[PlayerPage] promo fetch threw:', err);
			setPromoValid(false);
		}).finally(() => {
			setPromoChecked(true);
		});
	}, [promoToken, promoVideo]);

	// Handle public playback ID — fetch full video metadata then override muxVideoLink with the public playback URL
	useEffect(() => {
		if (!publicPlaybackId || !videoId) return;
		fetch(`${import.meta.env.VITE_APP_API_URL}/video/public/${videoId}`)
			.then(res => res.json())
			.then(data => {
				if (data && data.length > 0) {
					setPromoVideoData({
						...data[0],
						muxVideoLink: `https://stream.mux.com/${publicPlaybackId}.m3u8`,
						videoStoryboardToken: null
					});
				}
			})
			.catch(err => console.error('[PlayerPage] Error fetching public video:', err))
			.finally(() => setPromoChecked(true));
	}, [publicPlaybackId, videoId]);

	// Only run auth check when this is not a promo access URL.
	useAuthorizationForNonSubscribeUser(hasPromoAccess);

	const { data: swrVideoData = [], error: videoError } = useFetchVideoById(hasPromoAccess ? null : videoId);

	// Fallback redirect for TRC/LessonPlan content mistakenly routed to PlayerPage
	useEffect(() => {
		if (videoError && videoId && !hasPromoAccess) {
			const targetParam = searchParams.get('target');
			const targetQuery = targetParam ? `?target=${targetParam}` : '';
			navigate(`/trc/lesson-plans/${videoId}${targetQuery}`, { replace: true });
		}
	}, [videoError, videoId, hasPromoAccess, searchParams, navigate]);

	// Use promo video data if available, otherwise use SWR data
	const fetchedVideo = promoVideoData || swrVideoData?.[0] || null;
	const errorMessage = videoError?.response?.data?.error || 'An error occurred while fetching the video.';
	const videoCategory = fetchedVideo?.CategoryDetails?.[0]?.name || 'Uncategorized';

	if (hasPromoAccess && !promoChecked) {
		return (
			<PlayerPageLayouts fetchedVideo={null} isPromo={true}>
				<div>Verifying your free access...</div>
			</PlayerPageLayouts>
		);
	}

	if (hasPromoToken && promoChecked && !promoValid) {
		return (
			<PlayerPageLayouts fetchedVideo={null} isPromo={true}>
				<div>This promotional video link is invalid or expired.</div>
			</PlayerPageLayouts>
		);
	}

	// Block non-logged-in users only when there is no valid promo token or public playback ID
	if (!userData.token && !promoValid && !hasPublicPlaybackId) {
		return <MessageLayout message="Please log in to view the content." />;
	}

	if (videoError) {
		return <MessageLayout message={errorMessage} />;
	}

	if (!fetchedVideo) {
		return (
			<PlayerPageLayouts fetchedVideo={null} isPromo={hasPromoAccess}>
				<div>loading....</div>
			</PlayerPageLayouts>
		);
	}

	const isSubscriber = userData?.user?.userType?.toLowerCase() || userData?.role === userRoleTypes.ADMIN || promoValid || hasPublicPlaybackId;
	const isClassContent = typeOfContentView === ContentView.CLASSES || fetchedVideo?.type === 'class';
	const currentPath = `${location.pathname}${location.search}`;

	const handleUnlockFullLibrary = () => {
		localStorage.setItem(localStorageKeys.REDIRECT_AFTER_LOGIN, currentPath);
		const params = new URLSearchParams();
		if (promoToken) {
			params.set('promo', 'advanced-jumps');
			params.set('promoToken', promoToken);
		}
		navigate(params.toString() ? `/SignUp?${params.toString()}` : '/SignUp');
	};

	if (!isSubscriber && isClassContent) {
		return (
			<PlayerPageLayouts fetchedVideo={fetchedVideo}>
				<ClassVideoTrailer selectedVideo={fetchedVideo} errorMessage={errorMessage} />
			</PlayerPageLayouts>
		);
	}

	return (
		<PlayerPageLayouts fetchedVideo={fetchedVideo} isPromo={promoValid}>
			<MuxVideoPlayer
				CustomWrapper={PlayerPageWrapper}
				videoLink={fetchedVideo?.muxVideoLink}
				videoTitle={fetchedVideo?.title}
				poster={fetchedVideo?.thumnailLink}
				storyboardToken={fetchedVideo?.videoStoryboardToken}
				userId={userData?.user?._id || userData?.userId}
				videoId={fetchedVideo?._id}
				videoCategory={videoCategory}
				userEmail={userData?.user?.email}
				videoType={fetchedVideo?.type}
				chapters={fetchedVideo?.ChapterDetails ? fetchedVideo?.ChapterDetails[0]?.chapters : []}
				errorMessage={errorMessage}
			/>
			<VideoDescription
				selectedVideo={fetchedVideo}
				isPromo={promoValid}
				onUnlockFullLibrary={handleUnlockFullLibrary}
			/>
		</PlayerPageLayouts>
	);
}

export const PlayerPageWrapper = ({ children }) => <div className="c-video-player_frame">{children}</div>;

PlayerPageWrapper.propTypes = {
	children: PropTypes.node.isRequired
};

const MessageLayout = ({ title, description, message }) => (
	<Layout title={title} description={description}>
		<p>{message}</p>
	</Layout>
);

MessageLayout.propTypes = {
	title: PropTypes.string.isRequired,
	description: PropTypes.string.isRequired,
	message: PropTypes.string.isRequired
};

export default PlayerPage;
